/*
    Copyright (c) 2023 Alethea Katherine Flowers.
    Published under the standard MIT License.
    Full text available at: https://opensource.org/licenses/MIT
*/

import { DeferredPromise } from "../../../base/async";
import { delegate, listen } from "../../../base/events";
import { length } from "../../../base/iterator";
import {
  attribute,
  html,
  type ElementOrFragment,
} from "../../../base/web-components";
import { parseFlagAttribute } from "../../../base/web-components/flag-attribute";
import {
  KCUIActivitySideBarElement,
  KCUIButtonElement,
  KCUIElement,
} from "../../../kc-ui";
import { KiCanvasSelectEvent } from "../../../viewers/base/events";
import type { Viewer } from "../../../viewers/base/viewer";
import type { Project, ProjectPage } from "../../project";

// import dependent elements so they're registered before use.
import "./help-panel";
import "./preferences-panel";
import "./project-panel";
import "./viewer-bottom-toolbar";

interface ViewerElement extends HTMLElement {
  viewer: Viewer;
  load(src: ProjectPage): Promise<void>;
  disableinteraction: boolean;
}

export declare type ControlsType = "none" | "basic" | "basic+" | "full" | "";

/**
 * Common base class for the schematic, board, etc. apps.
 */
export abstract class KCViewerAppElement<
  ViewerElementT extends ViewerElement,
> extends KCUIElement {
  #viewer_elm: ViewerElementT;
  #activity_bar: KCUIActivitySideBarElement | null;

  project: Project;
  viewerReady: DeferredPromise<boolean> = new DeferredPromise<boolean>();
  viewerLoaded: DeferredPromise<boolean> = new DeferredPromise<boolean>();

  constructor() {
    super();
    this.provideLazyContext("viewer", () => this.viewer);
  }

  get viewerElement() {
    return this.#viewer_elm;
  }

  get viewer() {
    return this.#viewer_elm.viewer;
  }

  @attribute({ type: String })
  controls: ControlsType;

  @attribute({ type: String })
  controlslist: string;

  @attribute({ type: Boolean })
  sidebarcollapsed: boolean;

  override connectedCallback() {
    this.hidden = true;
    (async () => {
      this.project = await this.requestContext("project");
      await this.project.loaded;
      super.connectedCallback();
    })();
  }

  override initialContentCallback() {
    // If the project already has an active page, load it.
    if (this.project.active_page) {
      this.load(this.project.active_page!);
    }

    // Listen for changes to the project's active page and load or hide
    // as needed.
    this.addDisposable(
      listen(this.project, "change", async (e) => {
        const page = this.project.active_page;
        if (page) {
          await this.load(page);
        } else {
          this.hidden = true;
        }
      }),
    );

    // Handle item selection in the viewers.
    this.addDisposable(
      this.viewer.addEventListener(KiCanvasSelectEvent.type, (e) => {
        this.on_viewer_select(e.detail.item, e.detail.previous);
      }),
    );

    // Handle download button.
    delegate(this.renderRoot, "kc-ui-button", "click", (e) => {
      const target = e.target as KCUIButtonElement;
      console.log("button", target);
      switch (target.name) {
        case "download":
          if (this.project.active_page) {
            this.project.download(
              this.project.active_page.filename,
            );
          }
          break;
        default:
          console.warn("Unknown button", e);
      }
    });
  }

  protected abstract on_viewer_select(
    item?: unknown,
    previous?: unknown,
  ): void;

  protected abstract can_load(src: ProjectPage): boolean;

  async load(src: ProjectPage) {
    await this.viewerReady;
    if (this.can_load(src)) {
      await this.#viewer_elm.load(src);
      this.viewerLoaded.resolve(true);
      this.hidden = false;
    } else {
      this.hidden = true;
    }
  }

  #has_more_than_one_page() {
    return length(this.project.pages()) > 1;
  }

  protected make_pre_activities() {
    const activities = [];

    if (this.#has_more_than_one_page()) {
      activities.push(
        html`<kc-ui-activity
                    slot="activities"
                    name="Project"
                    icon="folder">
                    <kc-project-panel></kc-project-panel>
                </kc-ui-activity>`,
      );
    }

    return activities;
  }

  protected make_post_activities() {
    return [
      // Preferences
      html`<kc-ui-activity
                slot="activities"
                name="Preferences"
                icon="settings"
                button-location="bottom">
                <kc-preferences-panel></kc-preferences-panel>
            </kc-ui-activity>`,

      // Help
      html` <kc-ui-activity
                slot="activities"
                name="Help"
                icon="help"
                button-location="bottom">
                <kc-help-panel></kc-help-panel>
            </kc-ui-activity>`,
    ];
  }

  protected abstract make_activities(controls?: ControlsType): ElementOrFragment[];

  protected change_activity(name?: string) {
    this.#activity_bar?.change_activity(name);
  }

  protected abstract make_viewer_element(): ViewerElementT;

  override render() {
    const controls = this.controls ?? "none";
    const controlslist = parseFlagAttribute(
      this.controlslist ?? "",
      controls == "none"
        ? { fullscreen: false, download: false }
        : { fullscreen: true, download: true },
    );

    this.#viewer_elm = this.make_viewer_element();
    this.#viewer_elm.disableinteraction = controls == "none";

    let resizer = null;

    if (controls == "full" || controls == "basic+") {
      const pre_activities = this.make_pre_activities();
      const post_activities = this.make_post_activities();
      const activities = this.make_activities(controls);
      this.#activity_bar = html`<kc-ui-activity-side-bar
                collapsed="${this.sidebarcollapsed}">
                ${pre_activities} ${activities} ${post_activities}
            </kc-ui-activity-side-bar>` as KCUIActivitySideBarElement;
      resizer = html`<kc-ui-resizer></kc-ui-resizer>`;
    } else {
      // NO activity bar
      this.#activity_bar = null;
    }

    const top_toolbar_buttons = [];

    if (controlslist["download"] && !this.#has_more_than_one_page()) {
      top_toolbar_buttons.push(
        html`<kc-ui-button
                    slot="right"
                    name="download"
                    title="download"
                    icon="download"
                    variant="toolbar-alt">
                </kc-ui-button>`,
      );
    }

    const top_toolbar = html`<kc-ui-floating-toolbar location="top">
            ${top_toolbar_buttons}
        </kc-ui-floating-toolbar>`;

    let bottom_toolbar = null;
    if (controls != "none") {
      bottom_toolbar = html`<kc-viewer-bottom-toolbar></kc-viewer-bottom-toolbar>`;
    }

    return html`<kc-ui-split-view vertical>
            <kc-ui-view class="grow">
                ${top_toolbar} ${this.#viewer_elm} ${bottom_toolbar}
            </kc-ui-view>
            ${resizer} ${this.#activity_bar}
        </kc-ui-split-view>`;
  }

  override renderedCallback(): void | undefined {
    window.requestAnimationFrame(() => {
      this.viewerReady.resolve(true);
    });
  }
}
